import React from "react";
import { RankingPlayer } from "types/ranking";
import { getAvatarImage } from "utils/avatars";
import "./styles.scss";

// Importar imagens das bandeiras
import bandeiraDourada from "assets/img/bandeira_dourada.png";
import bandeiraPrata from "assets/img/bandeira_prata.png";
import bandeiraBronze from "assets/img/bandeira_bronze.png";
import bgVideo from "assets/video/bg.mp4";

interface BannerPodiumPreviewProps {
  title?: string;
  players: RankingPlayer[];
  isLoading?: boolean;
}

export const BannerPodiumPreview: React.FC<BannerPodiumPreviewProps> = ({
  title = "Top 3",
  players,
  isLoading = false,
}) => {
  // Garantir que temos exatamente 3 jogadores (preenchendo com vazios se necessário)
  const topThree = [...players];
  while (topThree.length < 3) {
    topThree.push({
      id: `empty-${topThree.length}`,
      name: "---",
      position: topThree.length + 1,
      points: 0,
    });
  }

  // Ordenar: 2º, 1º, 3º (ordem visual do pódio)
  const [first, second, third] = topThree.slice(0, 3);
  const podiumOrder = [second, first, third];

  const getBannerImage = (position: number): string => {
    switch (position) {
      case 1:
        return bandeiraDourada;
      case 2:
        return bandeiraPrata;
      case 3:
        return bandeiraBronze;
      default:
        return bandeiraDourada;
    }
  };

  const getPlayerInitials = (name: string): string => {
    if (!name || name === "---") return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (isLoading) {
    return (
      <div className="top3-banners-preview">
        <video className="ranking-bg-video" autoPlay loop muted playsInline>
          <source src={bgVideo} type="video/mp4" />
        </video>
        <div className="banners-preview-container relative -mt-[332px]">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Carregando ranking...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="top3-banners-preview">
      <video className="ranking-bg-video" autoPlay loop muted playsInline>
        <source src={bgVideo} type="video/mp4" />
      </video>
      <div className="banners-preview-container relative -mt-[332px]">
        {podiumOrder.map((player, index) => {
          const avatarSrc =
            getAvatarImage(player.fotoDePerfil || player.avatar) ||
            getAvatarImage(player.avatar);
          const isFirstPlace = player.position === 1;

          return (
            <div
              key={player.id}
              className={`banner-preview-item ${isFirstPlace ? "first-place" : ""}`}
            >
              <div className="banner-wrapper">
                <div className="position-number">{player.position}</div>
                <img
                  src={getBannerImage(player.position)}
                  alt={`${player.position}º Lugar`}
                  className="banner-preview-image"
                />
                <div className="player-overlay">
                  <div className="player-avatar-container">
                    {avatarSrc ? (
                      <img
                        src={avatarSrc}
                        alt={player.name}
                        className="player-avatar-img"
                      />
                    ) : (
                      <div className="player-avatar-placeholder">
                        {getPlayerInitials(player.name)}
                      </div>
                    )}
                  </div>
                  <div className="player-info">
                    <div className="name-ribbon">
                      <h3 className="player-name">{player.name}</h3>
                    </div>
                    <p className="player-points">
                      {player.points.toLocaleString("pt-BR")} pts
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
